import { parseNumber, round, toMinorUnits } from 'intl-utils';
import { onPageLoad } from 'runtime';
import { fetch, getName } from 'utils';

onPageLoad(import.meta, () => {
    const container = document.getElementById('stripe-express-container');
    const sessionData = JSON.parse(container.dataset.sessionData);
    const checkoutUrl = container.dataset.checkoutUrl ?? '/checkout';
    const updateEventName = container.dataset.updateEventName ?? 'cart:update';
    let paymentSummary = container.$data.paymentSummary ?? container.$data.cart.paymentSummary ?? {}; // Falling back to the cart.paymentSummary saves us from defining a paymentSummary property on the buy now or cart views (which may have already been overridden). Also the cart.paymentSummary is null when on the buy now view (product details page).

    // Reference: https://stripe.com/docs/payments/finalize-payments-on-the-server?platform=web&type=payment
    const stripe = Stripe(container.dataset.sessionId);

    let grandTotal = parseNumber(paymentSummary.grandTotal ?? '0');
    const elements = stripe.elements({
        mode: 'payment',
        currency: sessionData['Currency'].toLowerCase(),
        amount: toMinorUnits(grandTotal > 0 ? grandTotal : 1), // Note: Stripe will throw an error if the amount is 0.
        paymentMethodCreation: 'manual'
    });

    const expressCheckoutElement = elements.create('expressCheckout', {
        layout: {
            overflow: 'never'
        }
    });
    expressCheckoutElement.mount('#express-checkout-element');

    expressCheckoutElement.on('click', async event => {
        // Make sure the terms checkbox is checked (if applicable).
        const expressCheckoutTerms = document.querySelector('[name="ExpressCheckoutTerms"]');

        if (expressCheckoutTerms?.checked === false) {
            expressCheckoutTerms.classList.add('is-invalid');
            expressCheckoutTerms.focus();
            return;
        } else
            expressCheckoutTerms?.classList.remove('is-invalid');

        // Initially tried dispatching an event wrapped in a promise and resolving it within the event listener but then you must make sure the event listener is defined which isn't always the case.
        if (typeof window.initExpressCheckout === 'function' && await window.initExpressCheckout(container) === false)
            return;

        // Make sure a default delivery method is selected. If there is a delivery method id set then make sure it is in the available delivery methods.
        if (paymentSummary.isDeliveryRequired === true && (paymentSummary.deliveryMethodId == null || !paymentSummary.deliveryMethods.some(m => m.id == paymentSummary.deliveryMethodId)) && paymentSummary.deliveryMethods.length > 0)
            await setDeliveryMethod(paymentSummary.deliveryMethods[0].id);

        event.resolve({
            emailRequired: true,
            phoneNumberRequired: true,
            shippingAddressRequired: paymentSummary.isDeliveryRequired === true,
            shippingRates: paymentSummary.isDeliveryRequired === true ? paymentSummary.deliveryMethods
                .slice() // This copies the original data so it's not modified after sorting below.
                .sort((a, b) => (b.id == paymentSummary.deliveryMethodId) - (a.id == paymentSummary.deliveryMethodId)) // Sort the delivery methods since the first one is the one selected.
                .map(deliveryMethod => ({
                    id: deliveryMethod.id.toString(),
                    displayName: deliveryMethod.displayName ?? deliveryMethod.name,
                    amount: toMinorUnits(round(parseNumber(deliveryMethod.amount) + parseNumber(deliveryMethod.amountTax), 2)) // Note: We must call round since in JavaScript you get small imprecisions when performing arithmetic. For example: console.log(0.2 + 0.1); // Output: 0.30000000000000004.
                })) : []
        });
    });
    
    let deliveryAddressCountryCode = container.$data.deliveryAddress?.countryCode;
    let deliveryAddressPostcode = container.$data.deliveryAddress?.postcode;

    expressCheckoutElement.on('shippingaddresschange', async event => {
        // Set the delivery address (you must do this before validating).
        deliveryAddressCountryCode = event.address.country;
        deliveryAddressPostcode = event.address.postal_code;

        // Make sure the delivery method is valid for this address.
        if (!(await isDeliveryMethodValid(paymentSummary.deliveryMethodId))) {
            event.reject();
            return;
        }

        event.resolve();
    });

    expressCheckoutElement.on('shippingratechange', async event => {
        // Make sure the delivery method is valid.
        if (!(await isDeliveryMethodValid(event.shippingRate.id))) {
            event.reject();
            return;
        }

        // Set the delivery method.
        await setDeliveryMethod(event.shippingRate.id);

        event.resolve();
    });

    expressCheckoutElement.on('confirm', async event => {
        const { error: submitError } = await elements.submit();

        if (submitError) {
            console.warn(submitError);
            return;
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements
        });
        
        if (error) {
            console.warn(error);
            return;
        }

        const billingName = getName(event.billingDetails.name);

        const formData = new FormData();
        formData.append('Contact.Name.FirstName', billingName.firstName);
        formData.append('Contact.Name.LastName', billingName.lastName);
        formData.append('Contact.Email', event.billingDetails.email);
        formData.append('Contact.Telephone', event.billingDetails.phone);
        formData.append('IsDeliveryRequired', paymentSummary.isDeliveryRequired === true);
        formData.append('Delivery.DeliveryMethodId', paymentSummary.deliveryMethodId ?? '');

        if (paymentSummary.isDeliveryAddressRequired === true) {
            const deliveryName = getName(event.shippingAddress.name);
            const deliveryCountryResponse = await fetch(`/api/countries/${event.shippingAddress.address.country}`);
            const deliveryCountryId = (await deliveryCountryResponse.json()).id;

            formData.append('Delivery.IsNewDeliveryAddressRequired', true);
            formData.append('Delivery.NewDeliveryAddress.Name.FirstName', deliveryName.firstName);
            formData.append('Delivery.NewDeliveryAddress.Name.LastName', deliveryName.lastName ?? '');
            formData.append('Delivery.NewDeliveryAddress.AddressLine1', event.shippingAddress.address.line1);
            formData.append('Delivery.NewDeliveryAddress.AddressLine2', event.shippingAddress.address.line2);
            formData.append('Delivery.NewDeliveryAddress.Town', event.shippingAddress.address.city);
            formData.append('Delivery.NewDeliveryAddress.County', event.shippingAddress.address.state);
            formData.append('Delivery.NewDeliveryAddress.CountryId', deliveryCountryId);
            formData.append('Delivery.NewDeliveryAddress.Postcode', event.shippingAddress.address.postal_code);
        }

        formData.append('Payment.PaymentMethodId', container.dataset.paymentMethodId);
        formData.append('Payment.Token', paymentMethod.id);
        formData.append('Terms', true);

        // Create the order.
        const response = await fetch(`${checkoutUrl}?${paymentSummary.sessionId != null ? `sessionId=${encodeURIComponent(paymentSummary.sessionId)}&` : ''}isBillingAddressRequired=false&isCardDetailsRequired=false`, {
            method: 'POST',
            body: formData
        });
        const data = await response.json();

        window.location.href = data.redirectUrl ?? `${checkoutUrl}/complete${paymentSummary.sessionId != null ? `?sessionId=${encodeURIComponent(paymentSummary.sessionId)}` : ''}`;
    });

    // This makes sure the amount shown to the customer to pay is the final amount. See Apple Pay for an example.
    document.addEventListener(updateEventName, e => {
        grandTotal = parseNumber(e.detail.grandTotal);

        // This fixes an issue when emptying the cart.
        if (grandTotal == 0)
            return;

        // Reference: https://stripe.com/docs/js/elements_object/update#elements_update-options-amount
        elements.update({
            amount: toMinorUnits(grandTotal)
        });

        // Update the payment summary.
        paymentSummary = e.detail.paymentSummary;
    });

    async function isDeliveryMethodValid(id) {
        const response = await fetch(`/api/cart/validate-delivery-method?id=${id}&sessionId=${paymentSummary.sessionId != null ? encodeURIComponent(paymentSummary.sessionId) : ''}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
            method: 'POST'
        });

        return response.ok;
    }

    async function setDeliveryMethod(id) {
        const response = await fetch(`/api/cart/set-delivery-method?id=${id}&sessionId=${paymentSummary.sessionId != null ? encodeURIComponent(paymentSummary.sessionId) : ''}&deliveryAddressCountryCode=${deliveryAddressCountryCode ? encodeURIComponent(deliveryAddressCountryCode) : ''}&deliveryAddressPostcode=${deliveryAddressPostcode ? encodeURIComponent(deliveryAddressPostcode) : ''}`, {
            method: 'POST'
        });
        const data = await response.json();

        container.dispatchEvent(new CustomEvent(updateEventName, { bubbles: true, detail: data }));
    }
});